import { Form } from '@nick-mazuk/ui/components/form'
import { EmailInput } from '@nick-mazuk/ui/components/form/email-input'
import { NameInput } from '@nick-mazuk/ui/components/form/name-input'
import { TextInput } from '@nick-mazuk/ui/components/form/text-input'
import { TextAreaInput } from '@nick-mazuk/ui/components/form/textarea-input'
import { Accordion, AccordionGroup } from '@nick-mazuk/ui/elements/accordion'
import { Button } from '@nick-mazuk/ui/elements/button'
import { Check, Edit2 } from '@nick-mazuk/ui/elements/icon'
import { Link } from '@nick-mazuk/ui/elements/link'
import { Note } from '@nick-mazuk/ui/elements/note'
import { Text } from '@nick-mazuk/ui/elements/text'
import { TextContent } from '@nick-mazuk/ui/elements/text-content'
import { Layout } from 'components/layout'

const SuccessMessage = ({ state }: { state: string }): JSX.Element => {
    if (state === 'submitted') {
        return (
            <Note label='Hazzah' type='success' small>
                Got your message! You'll hear from me soon.
            </Note>
        )
    }
    if (state === 'error') {
        return (
            <Note label='Oh no' type='error' small>
                There was an internal error while sending your message. Try again. If it happens
                again, don't worry, Nick's already been notified about the error.
            </Note>
        )
    }
    // eslint-disable-next-line react/jsx-no-useless-fragment -- base case
    return <></>
}

export default function Contact(): JSX.Element {
    return (
        <Layout title='Contact'>
            <div className='max-w-2xl mx-auto wrapper'>
                <div className='flex flex-col w-full py-24 space-y-8'>
                    <Text h2 as='h1'>
                        Let's talk
                    </Text>
                    <Form
                        method='post'
                        action='/api/contact-submit'
                        clearOnSubmit
                        captcha='contact-form'
                    >
                        {(formSync) => (
                            <div className='flex flex-col space-y-4'>
                                <div className='grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-8'>
                                    <NameInput formSync={formSync} name='name' />
                                    <EmailInput formSync={formSync} />
                                </div>
                                <TextInput
                                    type='text'
                                    label='Subject'
                                    optional
                                    prefix={<Edit2 />}
                                    formSync={formSync}
                                />
                                <TextAreaInput
                                    label='Message'
                                    formSync={formSync}
                                    requiredMessage='Please write a message'
                                />
                                <div className='flex flex-col space-y-4 md:flex-row md:space-x-8 md:space-y-0'>
                                    <div className='flex flex-grow'>
                                        <SuccessMessage state={formSync.state} />
                                    </div>

                                    <div>
                                        <Button
                                            value='Send message'
                                            color='primary'
                                            icon={<Check />}
                                            disabled={formSync.state === 'submitting'}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </Form>
                    <div className='pt-4'>
                        <TextContent>
                            <Text small color='text-gray'>
                                To protect your privacy while also stopping bots, this form is
                                protected by hCaptcha instead of Google's RECAPTCHA. Its{' '}
                                <a href='https://hcaptcha.com/privacy'>Privacy Policy</a> and{' '}
                                <a href='https://hcaptcha.com/terms'>Terms of Service</a> apply.
                            </Text>
                        </TextContent>
                    </div>
                </div>

                <div className='pb-8'>
                    <Text h3 as='h2'>
                        FAQ
                    </Text>
                    <div className='mt-8'>
                        <AccordionGroup>
                            <Accordion title='When can I expect to hear back?' small>
                                <TextContent>
                                    I try to respond to everything within a day, but that's not
                                    always possible. If it's been a few days and I haven't
                                    responded, feel free to send a followup.
                                </TextContent>
                            </Accordion>
                            <Accordion title='Can you help with Finale?' small>
                                <TextContent>
                                    <p>
                                        Yes, I can! However, please check out my{' '}
                                        <Link href='https://youtube.com/FinaleSuperuser?subscribe-1'>
                                            YouTube channel
                                        </Link>{' '}
                                        and{' '}
                                        <Link href='https://finalesuperuser.com/blog'>blog</Link>{' '}
                                        first. Most of the time your question will be answered
                                        there. It's not uncommon for me to get half a dozen emails
                                        about Finale every day.
                                    </p>
                                    <p>
                                        However, if you can't find the answer to your question, go
                                        ahead and reach out.
                                    </p>
                                </TextContent>
                            </Accordion>
                            <Accordion title='How do I install your JW Lua scripts?' small>
                                <TextContent>
                                    <p>
                                        Literally the #1 question I get, so I've made many resources
                                        on the topic. Here are just two, though you can find more on
                                        the{' '}
                                        <Link href='https://youtube.com/FinaleSuperuser?subscribe-1'>
                                            YouTube channel
                                        </Link>{' '}
                                        and the{' '}
                                        <Link href='https://finalesuperuser.com/blog'>blog</Link>.
                                    </p>
                                    <ul>
                                        <li>
                                            <Link href='https://youtu.be/EFGNuGCEIq4'>Video 1</Link>
                                        </li>
                                        <li>
                                            <Link href='https://youtu.be/R8ti6nj832g'>Video 2</Link>
                                        </li>
                                    </ul>
                                    <p>
                                        There's also the{' '}
                                        <Link href='https://www.facebook.com/groups/742277119576336'>
                                            JW Lua Facebook page
                                        </Link>{' '}
                                        which I co-founded and you can get help there. Literally
                                        every prominent JW Lua user is there (including me). Odds
                                        are, if you ask there, you'll get a better and faster answer
                                        there.
                                    </p>
                                </TextContent>
                            </Accordion>
                            <Accordion title='Can you help with JW Lua?' small>
                                <TextContent>
                                    <p>
                                        Sure, but not here. Take a look at the{' '}
                                        <Link href='https://www.facebook.com/groups/742277119576336'>
                                            Facebook page
                                        </Link>
                                        . Literally every prominent JW Lua user is there (including
                                        me). Odds are, if you ask there, you'll get a better and
                                        faster answer there.
                                    </p>
                                    <p>
                                        If you want to learn to code in JW Lua, consider checking
                                        out the{' '}
                                        <Link href='https://www.youtube.com/playlist?list=PLsFZ0c2Wsoy9ZF6a0ZihC_-SPf3FkOh8o'>
                                            video series
                                        </Link>{' '}
                                        I made.
                                    </p>
                                </TextContent>
                            </Accordion>
                            <Accordion title='Can I ask a question about Parts and Score?' small>
                                <TextContent>
                                    Sure, but not here. Please go through{' '}
                                    <Link href='https://partsandscore.com'>Parts and Score</Link>.
                                </TextContent>
                            </Accordion>
                            <Accordion
                                title='Can I ask a question about the JetStream Finale Controller?'
                                small
                            >
                                <TextContent>
                                    Sure, but not here. Take a look at the{' '}
                                    <Link href='https://www.facebook.com/groups/jetstream.finale'>
                                        Facebook page
                                    </Link>
                                    . I'm there as well as hundreds of other JetStream users and the
                                    other creators. Odds are, if you ask there, you'll get a better
                                    and faster answer there.
                                </TextContent>
                            </Accordion>
                            <Accordion title='Can I hire you?' small>
                                <TextContent>
                                    <p>
                                        Yep! Send me what you need, the more detail the better. Then
                                        we can work out the specifics and rate.
                                    </p>
                                    <p>
                                        Do note that I'm typically very busy, so I may not be able
                                        to help you out. But I'll consider it.
                                    </p>
                                </TextContent>
                            </Accordion>
                            <Accordion
                                title='What if I want to contact you about something else?'
                                small
                            >
                                <TextContent>Please, send me a message!</TextContent>
                            </Accordion>
                        </AccordionGroup>
                    </div>
                </div>
            </div>
        </Layout>
    )
}
